import cn from 'classnames'
import { format as fnsFormat, toDate } from 'date-fns'
import React, { useMemo, useState } from 'react'
import ReactDatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

import Dropdown from 'components/uiKit/Dropdown/DropDown'
import { isMixed } from 'components/uiKit/KitUtils'
import { t } from 'services/Translation'
import { DATE_LOCALE } from 'services/Translation/i18n'

import type { IKitControlProps } from '../KitTypes'
import * as s from './DatePicker.module.scss'
import DatePickerDay from './DatePickerDay'
import DatePickerHeader from './DatePickerHeader'
import DatePickerInput from './DatePickerInput'

export interface IDefaultDatePicker extends IKitControlProps<number | null> {
  placeholder?: string
  styleType?: 'base' | 'ghost'
  fluid?: boolean
  format?: string
  minDate?: Date | number
  maxDate?: Date | number
}

const DatePicker = ({
  defaultValue,
  value,
  styleType,
  onChange,
  size,
  name,
  placeholder = t('input.placeholder.date'),
  format = 'dd.MM.yyyy',
  minDate,
  maxDate,
  disabled,
  error,
  fluid,
}: IDefaultDatePicker) => {
  const safeValue = isMixed(value) ? null : (value as number | null | undefined)
  const safeDefault = isMixed(defaultValue) ? null : (defaultValue as number | null | undefined)
  const selected = useMemo(
    () => (safeValue && toDate(safeValue)) || (safeDefault && toDate(safeDefault)) || null,
    [safeValue, safeDefault],
  )

  const [open, setOpen] = useState(false)

  const handleOnChange = (value: Date) => {
    setOpen(false)
    onChange?.(value.getTime())
  }

  return (
    <div className={cn(s.root, { [s.fluid]: fluid })}>
      <Dropdown
        closeOnRootClick={false}
        disabled={disabled}
        onVisibleChange={setOpen}
        overlay={
          <ReactDatePicker
            calendarClassName={s.calendar}
            // dayClassName={(_date) => s.day}
            disabled={disabled}
            locale={DATE_LOCALE}
            maxDate={maxDate ? toDate(maxDate) : null}
            minDate={minDate ? toDate(minDate) : null}
            onChange={handleOnChange}
            renderCustomHeader={(args) => <DatePickerHeader {...args} selected={selected} />}
            renderDayContents={(day, date) => (
              <DatePickerDay
                date={date}
                day={day}
                maxDate={maxDate}
                minDate={minDate}
                selected={selected}
              />
            )}
            selected={selected}
            inline
          />
        }
        overlayStyle={{ minWidth: 'unset' }}
        styleType='clear'
        visible={open}
      >
        <DatePickerInput
          disabled={disabled}
          error={error}
          name={name}
          onChange={onChange}
          placeholder={placeholder}
          size={size}
          styleType={styleType}
          value={(selected && fnsFormat(selected, format)) || ''}
        />
      </Dropdown>
    </div>
  )
}

export default DatePicker
